import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2 } from 'lucide-react';
import { searchApi } from '../services/searchApi';
import { useSelectedCity } from '../contexts/SelectedCityContext';

const MD_BREAKPOINT = 768;

const SearchModal = ({ isOpen, onClose, searchType = 'all', desktopAnchorRef }) => {
   const { selectedCity } = useSelectedCity();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [desktopPanelRect, setDesktopPanelRect] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setDesktopPanelRect(null);
      return;
    }

    const updatePosition = () => {
      if (
        desktopAnchorRef?.current &&
        typeof window !== 'undefined' &&
        window.innerWidth >= MD_BREAKPOINT
      ) {
        const r = desktopAnchorRef.current.getBoundingClientRect();
        setDesktopPanelRect({
          top: r.bottom + 8,
          left: r.left,
          width: r.width,
        });
      } else {
        setDesktopPanelRect(null);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, desktopAnchorRef]);

  const isDesktopPanel = desktopPanelRect !== null;

  // Load recent searches on component mount
  useEffect(() => {
    if (isOpen) {
      const recent = searchApi.getRecentSearches();
      setRecentSearches(recent.map((text, index) => ({ 
        id: index + 1, 
        text, 
        type: text.toLowerCase().includes('event') ? 'events' : 'stays' 
      })));
    }
  }, [isOpen]);

  // Debounced search function
  const performSearch = async (query) => {
    if (!query || query.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      setShowSuggestions(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await searchApi.searchAll(query, {
        type: searchType,
        selectedCity
      });

      // Transform results to match our display format
      const transformedResults = [];
      
      // Add events
      if (results.events && results.events.length > 0) {
        results.events.forEach(event => {
          console.log('Processing event:', event);
          console.log('Event price data:', event.price);
          transformedResults.push({
            id: `event-${event.eventId || event.id}`,
            title: event.name || event.title,
            description: event.description ? event.description.substring(0, 50) + '...' : '',
            date: event.startDate ? new Date(event.startDate).toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            }) : 'Date TBD',
            price: event.price?.amount ? `₹${event.price.amount} onwards` : 
                   event.price ? `₹${event.price} onwards` : 'Price TBD',
            image: event.images && event.images.length > 0 ? event.images[0] : '/assets/dog-help.gif',
            type: 'event',
            originalData: event
          });
        });
      }

      // Add stays
      if (results.stays && results.stays.length > 0) {
        results.stays.forEach(stay => {
          console.log('Processing stay:', stay);
          console.log('Stay price data:', stay.rooms?.[0]?.price);
          transformedResults.push({
            id: `stay-${stay.stayId || stay.id}`,
            title: stay.name || stay.title,
            description: stay.description ? stay.description.substring(0, 50) + '...' : '',
            date: 'Available Now',
            price: stay.rooms && stay.rooms.length > 0 && stay.rooms[0].price?.discountedPrice ? 
                   `₹${stay.rooms[0].price.discountedPrice} per night` :
                   stay.price ? `₹${stay.price} per night` : 'Price TBD',
            image: stay.images && stay.images.length > 0 ? stay.images[0] : '/assets/f1.png',
            type: 'stay',
            originalData: stay
          });
        });
      }

      setSearchResults(transformedResults);
      setShowResults(true);
      setShowSuggestions(false);

      // Save to recent searches
      searchApi.saveRecentSearch(query);

    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search. Please try again.');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Re-run search when the header city changes so stay results stay in sync
  useEffect(() => {
    if (!isOpen || !searchQuery.trim()) return;
    performSearch(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Debounce timer for search
  const [searchTimeout, setSearchTimeout] = useState(null);

  const handleSearch = (query) => {
    setSearchQuery(query);
    performSearch(query);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (value.length > 0) {
      // Debounce search by 500ms
      const timeout = setTimeout(() => {
        performSearch(value);
      }, 500);
      setSearchTimeout(timeout);
    } else {
      setShowSuggestions(true);
      setShowResults(false);
      setSearchResults([]);
    }
  };

  const handleBackClick = () => {
    if (showResults) {
      setShowResults(false);
      setShowSuggestions(true);
      setSearchQuery('');
      setSearchResults([]);
      setError(null);
    } else {
      onClose();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setShowResults(false);
    setShowSuggestions(true);
    setSearchResults([]);
    setError(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle result click
  const handleResultClick = (result) => {
    if (result.type === 'event') {
      const eventId = result.originalData.eventId || result.originalData.id;
      console.log('Navigating to event:', eventId, 'from data:', result.originalData);
      navigate(`/event/${eventId}`);
    } else if (result.type === 'stay') {
      const stayId = result.originalData.stayId || result.originalData.id;
      console.log('Navigating to stay:', stayId, 'from data:', result.originalData);
      navigate(`/property/${stayId}`);
    }
    onClose();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  if (!isOpen) return null;

  const backArrowButton = (
    <button
      type="button"
      onClick={handleBackClick}
      className={
        isDesktopPanel
          ? 'shrink-0 p-1 text-gray-600 hover:text-gray-900'
          : 'absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600'
      }
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <g clipPath="url(#clip0_3265_281)">
          <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <defs>
          <clipPath id="clip0_3265_281">
            <rect width="24" height="24" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </button>
  );

  const clearQueryButton = searchQuery ? (
    <button
      type="button"
      onClick={handleClearSearch}
      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <g clipPath="url(#clip0_3265_344)">
          <path d="M15 5L5 15" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 5L15 15" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <defs>
          <clipPath id="clip0_3265_344">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </button>
  ) : null;

  const searchBarSection = isDesktopPanel ? (
    <div className="shrink-0 border-b border-[#E5E3DF] bg-[#FAF7F2] p-4">
      <div className="flex items-center gap-2">
        {showResults ? backArrowButton : <span className="w-6 shrink-0" aria-hidden />}
        <div className="relative min-w-0 flex-1">
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            placeholder="Search for 'Stays'"
            className="h-[48px] w-full rounded-[14px] border border-[#FF5E4C] bg-transparent px-3 pr-10 text-[14px] text-black placeholder-gray-500 focus:outline-none focus:ring-0 focus:ring-[#FF5E4C]"
          />
          {clearQueryButton}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg p-2 text-gray-600 hover:bg-black/5 hover:text-gray-900"
          aria-label="Close search"
        >
          <X size={22} />
        </button>
      </div>
    </div>
  ) : (
    <div className="bg-[#FAF7F2] p-[20px]">
      <div className="relative">
        {backArrowButton}
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          placeholder="Search for 'Stays'"
          className="h-[48px] w-full rounded-[14px] border border-[#FF5E4C] bg-transparent pl-10 pr-[12px] text-[14px] text-black placeholder-gray-500 focus:outline-none focus:ring-0 focus:ring-[#FF5E4C]"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <g clipPath="url(#clip0_3265_344_m)">
                <path d="M15 5L5 15" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 5L15 15" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_3265_344_m">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  const suggestionsSection =
    showSuggestions && (
      <div className={isDesktopPanel ? 'p-4' : 'p-[20px]'}>
        <h3 className="mb-3 text-[20px] font-medium tracking-[-1px] text-black">Recent Searches</h3>
        <div className="space-y-[4px]">
          {recentSearches.map((search) => (
            <button
              key={search.id}
              type="button"
              onClick={() => handleSearch(search.text)}
              className="flex items-center space-x-[4px] rounded-[14px] border border-[#eaeaea] bg-white p-[4px] pr-[8px] transition-colors hover:bg-gray-200"
            >
              <div className="h-[40px] w-[40px] rounded-[10px] bg-[#F0F0F0]" />
              <span className="text-[14px] font-medium text-black">{search.text}</span>
            </button>
          ))}
        </div>
      </div>
    );

  const resultsSection =
    showResults && (
      <div className={isDesktopPanel ? 'p-4' : 'p-[20px]'}>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="mr-2 h-6 w-6 animate-spin text-gray-400" />
            <span className="text-gray-500">Searching...</span>
          </div>
        ) : error ? (
          <div className="py-8 text-center">
            <div className="mb-2 text-red-500">⚠️</div>
            <p className="text-red-500">{error}</p>
          </div>
        ) : searchResults.length > 0 ? (
          <div className="space-y-[20px]">
            {searchResults.map((result) => (
              <div
                key={result.id}
                role="button"
                tabIndex={0}
                onClick={() => handleResultClick(result)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleResultClick(result);
                }}
                className="cursor-pointer overflow-hidden rounded-[20px] bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex">
                  <div className="aspect-square w-[50%] flex-shrink-0 bg-gray-200">
                    <img
                      src={result.image}
                      alt={result.title}
                      className="h-full w-full rounded-l-lg object-cover"
                      onError={(e) => {
                        e.target.src = result.type === 'event' ? '/assets/dog-help.gif' : '/assets/f1.png';
                      }}
                    />
                  </div>
                  <div className="flex flex-col items-between p-[12px]">
                    <div className="flex-1">
                      <div className="mb-[12px] text-[14px] leading-none text-[#FF5E4C]">{result.date}</div>
                      <h3 className="text-[18px] font-medium leading-none tracking-[-0.4px] text-black">{result.title}</h3>
                    </div>
                    <div className="text-[14px] font-medium text-black">{result.price}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Search className="mx-auto mb-2 h-8 w-8 text-gray-400" />
            <p className="text-gray-500">No results found for &quot;{searchQuery}&quot;</p>
          </div>
        )}
      </div>
    );

  const scrollableBody = (
    <>
      {suggestionsSection}
      {resultsSection}
    </>
  );

  if (isDesktopPanel) {
    return (
      <>
        <div
          className="fixed inset-0 z-[45] bg-black/25"
          onClick={onClose}
          aria-hidden
        />
        <div
          className="fixed z-[50] flex max-h-[min(70vh,560px)] flex-col overflow-hidden rounded-2xl border border-[#E5E3DF] bg-[#FAF7F2] shadow-2xl"
          style={{
            top: desktopPanelRect.top,
            left: desktopPanelRect.left,
            width: desktopPanelRect.width,
          }}
        >
          {searchBarSection}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{scrollableBody}</div>
        </div>
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-[#FAF7F2]">
      {searchBarSection}
      {scrollableBody}
    </div>
  );
};

export default SearchModal;
